import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { Drop, MinimalDrop } from "@anaconda/core/drop/types"
import type { CollectionMiniature } from "@anaconda/core/nft-collection/types"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type {
  BlockchainBalanceSDK,
  OrderId,
} from "@rarible_int/anaconda_sdk/ethereum"
import { atom, computed } from "@reatom/core"
import { createScope, molecule } from "bunshi"
import type { PublicClient } from "viem"
import { BlockchainsMoleculeReatom as BlockchainsMolecule } from "../blockchains/blockchain-molecule.reatom"
import { BlockchainFiltersMoleculeReatom as BlockchainFiltersMolecule } from "../blockchains/filters/blockchain-filters-molecule.reatom"

// Legacy: This is no longer used - balance SDK is now provided by BlockchainBalanceSdkProviderMoleculeReatom
export const defaultBlockchainBalanceAtom =
  null as unknown as BlockchainBalanceSDK

// Blockchain-agnostic balance SDK scope (for read operations on any blockchain)
// Legacy: Use BlockchainBalanceSdkProviderMoleculeReatom instead
export const BlockchainBalanceSDKScope =
  createScope<BlockchainBalanceSDK | null>(null)

// Blockchain-specific client scopes (for read operations on any blockchain)
export const BlockchainPublicClientScope = createScope<PublicClient | null>(
  null,
)
export const BlockchainScope = createScope<Blockchain | undefined>(undefined)

export const OrderIdScope = createScope<OrderId | undefined>(undefined)
export const CollectionScope = createScope<CollectionMiniature | undefined>(
  undefined,
)

export const NftMoleculeReatom = molecule((mol, scope) => {
  const nft = scope(SDKNFTScope)
  const nftAtom = atom(nft, "nftAtom")
  const blockchainAtom = computed(() => {
    if (!nft) {
      throw new Error("NFT is not defined for molecule")
    }
    const blockchain = nft.collection.blockchain
    return blockchain
  }, "blockchainAtom")

  return {
    nftAtom,
    blockchainAtom,
  }
})

/**
 * ViewBlockchainMoleculeReatom - Provides scoped view context for blockchain operations
 *
 * This molecule derives the target blockchain and chain ID from either:
 * 1. NFT in scope (if component has NFT scope) - for component-level override
 * 2. URL blockchain parameter (via selectedBlockchain) - for page-level context
 *
 * Example use cases:
 * - Normal page `/ethereum/items/123` → viewBlockchainNetworkId = 1
 * - Test panel "Buy on Ethereum" button with ethereumTestNft scope → viewBlockchainNetworkId = 1
 * - Test panel "Buy on Base" button with baseTestNft scope → viewBlockchainNetworkId = 8453
 *
 * Compare viewBlockchainNetworkId with walletBlockchainNetworkId to determine
 * if a chain switch is needed before executing transactions.
 */
export const ViewBlockchainMoleculeReatom = molecule((mol, scope) => {
  const nft = scope(SDKNFTScope)
  const { selectedBlockchain } = mol(BlockchainFiltersMolecule)
  const { reatomChainId } = mol(BlockchainsMolecule)

  // Use NFT blockchain if in scope, otherwise fall back to URL blockchain
  const viewBlockchain = computed(() => {
    if (nft) {
      return nft.collection.blockchain
    }
    const selectedBlockchainValue = selectedBlockchain()
    if (selectedBlockchainValue === "ALL") {
      return undefined
    }
    return selectedBlockchainValue
  }, "viewBlockchain")

  // Convert blockchain to chain ID
  const viewBlockchainNetworkId = computed(() => {
    const blockchain = viewBlockchain()
    if (!blockchain) {
      return undefined
    }
    return reatomChainId()(blockchain)
  }, "viewBlockchainNetworkId")

  return {
    viewBlockchain,
    viewBlockchainNetworkId,
  }
})

export const BlockchainClientMoleculeReatom = molecule((mol, scope) => {
  const blockchain = scope(BlockchainScope)
  const publicClient = scope(BlockchainPublicClientScope)

  const blockchainAtom = atom(blockchain, "blockchainAtom")
  const publicClientAtom = atom(publicClient, "publicClientAtom")

  return {
    blockchainAtom,
    publicClientAtom,
  }
})

export const MinimalDropScope = createScope<MinimalDrop | undefined>(undefined)
export const SDKNFTScope = createScope<NftMiniature | undefined>(undefined)
